import { Fragment } from 'react'
import { Box, Typography, TypographyProps } from '@mui/material'
import { formatString } from '../util/formatString'
import { SPACE } from '../styles/layout'

/**
 * A line break in content marks a paragraph break. Content comes from the API
 * with either a blank line or a single newline between sentences; a single
 * newline left as a hard break staggers against natural wrapping at some
 * widths, so both become a paragraph gap.
 */
const PARAGRAPH_BREAK = /[ \t]*\n[ \t\n]*/

const ZText = (props: TypographyProps) => {
  const { children, ...rest } = props
  // A word with no break opportunity (a long name, a reference, a URL) must
  // wrap inside the column rather than overflow it, so copy from the API can
  // never push the layout off screen.
  const { sx, ...restWithoutSx } = rest

  // Multi-paragraph copy: each paragraph is its own block with a DS spacing
  // step between them. Text keeps the DS line height; only the gap between
  // paragraphs is a spacing token (space.xl), one step below the 24px between
  // blocks and two below the 32px above a new section.
  if (typeof children === 'string' && PARAGRAPH_BREAK.test(children)) {
    const paragraphs = children.split(PARAGRAPH_BREAK)
    return (
      <Typography
        component="div"
        whiteSpace="pre-wrap"
        sx={{ overflowWrap: 'anywhere', ...(sx as object) }}
        {...restWithoutSx}
      >
        {paragraphs.map((paragraph, index) => (
          <Box
            key={index}
            component="p"
            sx={{ m: 0, mb: index < paragraphs.length - 1 ? SPACE.xl : 0 }}
          >
            {formatString(paragraph)}
          </Box>
        ))}
      </Typography>
    )
  }

  const formatChildren = () => {
    if (typeof children === 'string') {
      return formatString(children)
    }

    // The learn-more-link path passes a reactStringReplace array mixing plain
    // strings (which may carry *bold*/~italic~/_underline_ markup) and elements
    // (e.g. <Link>). Run formatString on the string segments so both render.
    if (Array.isArray(children)) {
      return children.map((child, index) =>
        typeof child === 'string' ? (
          <Fragment key={index}>{formatString(child)}</Fragment>
        ) : (
          child
        ),
      )
    }

    return children
  }

  return (
    <Typography
      whiteSpace="pre-wrap"
      sx={{ overflowWrap: 'anywhere', ...(sx as object) }}
      {...restWithoutSx}
    >
      {formatChildren()}
    </Typography>
  )
}

export default ZText
